import { Box, Button, useTheme } from "@mui/material";
import { observer } from "mobx-react";
import { FC } from "react";

import { DownloadIcon } from "../../icons";
import FileItemMenu from "./FileItemMenu";
import { useChatStore } from "../../../hooks/useChatStore";
import useNotifier from "../../../hooks/useNotifier";
import { ChatLetterOutput } from "../../../types/chat";

const DOWNLOAD_BUTTON_SX = {
  display: "inline-flex",
  alignItems: "center",
  color: "text.primary",
  height: "25px",
  minWidth: 0,
} as const;

/** Keeps each row's "..." menu button hidden until the row is hovered/focused,
 * except while its own menu is open (it would otherwise vanish once the
 * pointer leaves the row for the open menu). */
const FILE_ITEM_MENU_VISIBILITY_SX = {
  "& .file-item-menu-button": { opacity: 0 },
  "&:hover .file-item-menu-button, &:focus-within .file-item-menu-button, & .file-item-menu-button.menu-open":
    { opacity: 1 },
} as const;

interface LetterRowProps {
  letter: ChatLetterOutput;
  isDownloading: boolean;
  isClosed: boolean;
  onSelect: () => void;
  onDownload: () => void;
}

/** Row for the generated appeal letter entry in {@link SidebarFileViewer}. */
const LetterRow: FC<LetterRowProps> = observer(({
  letter,
  isDownloading,
  isClosed,
  onSelect,
  onDownload,
}) => {
  const theme = useTheme();
  const chatStore = useChatStore();
  const notifier = useNotifier();
  const displayName = letter.display_name || "Appeal for Denied Claim";

  const handleTogglePin = async () => {
    const success = await chatStore.togglePinChatLetter(letter);
    if (!success) {
      notifier.showError("Failed to update the letter's pin status");
    }
  };

  // The name itself is entered in RenameFileDialog, which saves it through
  // chatStore.updateChatLetterName.
  const handleRename = () => {
    chatStore.setShowRenameFilePopup({
      kind: "letter",
      letter,
      currentName: displayName,
    });
  };

  return (
    <Box
      onClick={onSelect}
      title={`Click to view: ${displayName}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: 1,
        py: 0.5,
        borderRadius: 1,
        cursor: "pointer",
        "&:hover": { bgcolor: "grey.50" },
        ...FILE_ITEM_MENU_VISIBILITY_SX,
      }}
    >
      <Button
        variant="text"
        size="small"
        aria-label={`Download ${displayName}`}
        title="Download appeal (PDF + DOCX)"
        disabled={isDownloading}
        onClick={async (e) => {
          e.stopPropagation();
          await onDownload();
        }}
        sx={{
          ...DOWNLOAD_BUTTON_SX,
          "&:hover": {
            color: theme.palette.custom.brandTeal,
            backgroundColor: "transparent",
            "& svg path": { fill: "currentColor" },
          },
        }}
      >
        <DownloadIcon size={13} />
      </Button>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          color: "#2D2D2D",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: "0.875rem",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {displayName}
      </Box>
      {!isClosed && (
        <FileItemMenu
          isPinned={letter.is_pinned}
          ariaLabel={`More options for ${displayName}`}
          itemLabel="letter"
          onTogglePin={handleTogglePin}
          onRename={handleRename}
        />
      )}
    </Box>
  );
});

export default LetterRow;
